// frontend/src/pages/auth/ForgotPasswordPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CssBaseline,
  InputAdornment,
  Fade,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { authAPI } from '../../services/api';

// Import your logo
import syncroLogo from '../../assets/syncro_logo_color.svg';

const GradientBackground = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f0f4f8 0%, #e6ebee 100%)',
  padding: 16,
});

const StyledPaper = styled(Paper)({
  padding: 32,
  borderRadius: 20,
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 12px 32px rgba(31, 38, 135, 0.12)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.7)',
});

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await authAPI.forgotPassword({ email });
      setMessage('Password reset link has been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GradientBackground>
      <CssBaseline />
      <StyledPaper elevation={0}>
        <Fade in={true} timeout={500}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src={syncroLogo}
              alt="Syncro Logo"
              sx={{
                height: 72,
                objectFit: 'contain'
              }}
            />
            <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ mt: 1, mb: 1 }}>
              Forgot Password?
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Enter your email and we'll send you a link to reset your password.
            </Typography>
          </Box>
        </Fade>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{message}</Alert>}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 2,
              mb: 2,
              py: 1.2,
              background: 'linear-gradient(135deg, #4361ee, #3a0ca3)',
              color: 'white',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '16px',
              borderRadius: '10px',
              boxShadow: '0 4px 16px rgba(67, 97, 238, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3a50e2, #2d0a9a)',
                boxShadow: '0 6px 20px rgba(67, 97, 238, 0.4)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #a0aec0, #718096)',
                boxShadow: 'none',
              }
            }}
            disabled={loading}
            endIcon={<SendIcon />}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Link 
              to="/login" 
              style={{ 
                color: '#4361ee', 
                textDecoration: 'none',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <BackIcon fontSize="small" /> Back to Login
            </Link>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          textAlign: 'center', 
          mt: 4, 
          pt: 2, 
          borderTop: '1px solid #e2e8f0',
          color: '#718096',
          fontSize: '0.8rem'
        }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Syncro. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Designed & Developed by{' '}
            <a
              href="https://www.softsolutionmedia.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4361ee', textDecoration: 'none' }}
            >
              Soft Solution Media
            </a>
          </Typography>
        </Box>
      </StyledPaper>
    </GradientBackground>
  );
};

export default ForgotPasswordPage;